/* eslint-disable class-methods-use-this */
/* global logger */
const { freightUnit, indentStatus } = require('../../constants/payments');
const { orderType, deliveryTypes: DeliveryType, InvertedMovements } = require('pando-constants');
const { sortBy, isEmpty, cloneDeep } = require('lodash');
const Helper = require('./utils/helper');

const volumetricWeightAtMaterialLevelFreightUnits = [freightUnit.per_kg];

class Freight {
  static getMaxOfSKUMinRate(materialCategoriesGroup, contractCharge) {
    const skuCategoryRates = contractCharge.skuCategoryRates || [];
    const skuCategoryRatesMap = skuCategoryRates.reduce((acc, rate) => {
      acc[rate.product_category] = rate;
      return acc;
    }, {});

    const knownCategories = materialCategoriesGroup.reduce((acc, category) => {
      if (skuCategoryRatesMap[category]) {
        acc.push(category);
      }
      return acc;
    }, []);
    // isKnownCategoriesPresent - represents that all incoming materails having atleast
    // one product category from the contract
    const isKnownCategoriesPresent = knownCategories.length > 0;
    let maxofSkuCategoryMinRates = 0;
    if (skuCategoryRates.length) {
      if (!isKnownCategoriesPresent) {
        logger.info('[PAY] - All are unknown categories and selecting default min rate');
        // all categories are unknown
        if (skuCategoryRatesMap.default) {
          // choose default category min rate
          maxofSkuCategoryMinRates = skuCategoryRatesMap.default.min_rate;
        } else {
          // when there is no category called default choose minRate from contract.
          maxofSkuCategoryMinRates = contractCharge.minRate;
        }
      } else {
        logger.info('[PAY] - There are known categories and selecting default min rate among them');
        // logger.info('knownCategories - ',knownCategories);
        // there are some known categories.
        maxofSkuCategoryMinRates = knownCategories.reduce((acc, knownCategory) => {
          const skuRate = skuCategoryRatesMap[knownCategory];
          // logger.info('skuRate -  ',skuRate);
          // logger.info('Math.max(skuRate.min_rate, acc); -  ',Math.max(skuRate.min_rate, acc));
          return Math.max(skuRate.min_rate, acc);
        }, 0);
      }
    }
    logger.info(`[PAY] - Selected maxofSkuCategoryMinRates - ${maxofSkuCategoryMinRates}`);
    return maxofSkuCategoryMinRates;
  }

  static getFreightBasedOnSlab(materialQuantity, freightCategory, isRtoCharge = false, params = {}) {
    const { ignoreMinRate = false } = params;
    let quantity = materialQuantity;
    let index = 0;
    let freightCharge = 0;
    const slabs = [];

    while (quantity > 0 && index < freightCategory.length) {
      const slabCategory = freightCategory[index];
      const rate = isRtoCharge ? slabCategory.rto_charges : slabCategory.rate;
      const slabRange = slabCategory.slab.max - slabCategory.slab.min;
      const minRtoCharge = slabCategory.minimum_rto_charges ? slabCategory.minimum_rto_charges : 0;
      slabs.push(slabCategory);

      // For product category without slabs
      if (!slabRange) {
        if (isRtoCharge && minRtoCharge) {
          return { freightCharge: Math.max(minRtoCharge, (quantity * rate)), slabs };
        }
        return { freightCharge: (quantity * rate), slabs };
      }
      const charge = (slabRange <= quantity) ? (slabRange * rate) :
        (quantity * rate);

      // Minimum comparison only for first slab
      if (isRtoCharge) {
        freightCharge += Math.max(minRtoCharge, charge);
      } else {
        freightCharge += (index === 0 && !ignoreMinRate)
          ? Math.max(slabCategory.min_rate, charge)
          : charge;
      }
      quantity -= slabRange;
      index += 1;
    }

    // Take last slab for remaining cases
    if (quantity > 0) {
      const slabCategory = freightCategory[freightCategory.length - 1];
      const rate = isRtoCharge ? slabCategory.rto_charges : slabCategory.rate;
      const minRtoCharge = slabCategory.minimum_rto_charges ? slabCategory.minimum_rto_charges : 0;
      const charge = (quantity * rate);
      if (isRtoCharge) {
        freightCharge += Math.max(minRtoCharge, charge);
      } else {
        freightCharge += (freightCategory.length === 1 && !ignoreMinRate) ?
          Math.max(slabCategory.min_rate, charge) : charge;
      }
    }
    return { freightCharge, slabs };
  }

  // Calculating freight charge for per-kg
  static getFreightBasedOnSlabPerKg(
    materialQuantity,
    freightCategory = [],
    isRtoCharge = false,
    params = {},
  ) {
    const { ignoreMinRate = false } = params;
    let index = 0;
    let freightCharge = 0;
    const slabs = [];
    while (index < freightCategory.length) {
      const slabCategory = freightCategory[index];
      const rate = isRtoCharge ? slabCategory.rto_charges : slabCategory.rate;
      const minRtoCharge = slabCategory.minimum_rto_charges ? slabCategory.minimum_rto_charges : 0;
      slabs.push(slabCategory);

      // For product category without slabs
      if (materialQuantity >= slabCategory.slab.min && materialQuantity < slabCategory.slab.max) {
        const flatVariableCharge = slabCategory.flat_variable_charge || 0;
        return {
          ...isRtoCharge ? { freightCharge: Math.max(minRtoCharge, (materialQuantity * slabCategory.rto_charges)) } : {
            freightCharge: Math.max(
              ((slabCategory.min_rate && !ignoreMinRate) ? slabCategory.min_rate : 0),
              materialQuantity * rate,
            ) + flatVariableCharge,
          },
          slabs,
        };
      }
      index += 1;
    }

    // Take last slab for remaining cases
    const slabCategory = freightCategory[freightCategory.length - 1] || {};
    const rate = isRtoCharge ? slabCategory.rto_charges : slabCategory.rate;
    const flatVariableCharge = slabCategory.flat_variable_charge || 0;
    const minRtoCharge = slabCategory.minimum_rto_charges ? slabCategory.minimum_rto_charges : 0;
    const charge = (materialQuantity * rate);
    if (isRtoCharge) {
      freightCharge += Math.max(minRtoCharge, charge);
    } else {
      freightCharge += (freightCategory.length === 1 && !ignoreMinRate)
        ? Math.max((slabCategory.min_rate ? slabCategory.min_rate : 0), charge)
        : charge;
      freightCharge += flatVariableCharge;
    }
    return { freightCharge, slabs };
  }

  getContractBasedOnSlabs(skuCategoryRates, category, indentMovement) {
    let freightCategory = skuCategoryRates.filter(rate =>
      rate.product_category === category && rate.movement === indentMovement);
    if (!freightCategory.length) {
      freightCategory = skuCategoryRates.filter(rate =>
        rate.product_category === category && rate.movement === 'default');
    }
    return freightCategory || [];
  }

  calculateForwardTotalCostAtMaterialLineItemLvl(
    contract,
    groupByLineItem,
    skuCategoryRates,
    indentMovement,
    paymentSettings,
    configOptions,
    _params,
  ) {
    const params = _params;
    let materialCost = 0;
    const lineItemGroups = Object.keys(groupByLineItem);
    if ((paymentSettings && paymentSettings.product_category_based_on_slab)
      || contract.freightUnit === freightUnit.per_kg) {
      materialCost = lineItemGroups.map((lineItemKey) => {
        // eslint-disable-next-line prefer-const
        let { quantity, volume, weight } = groupByLineItem[lineItemKey].reduce((acc, item) => {
          acc.quantity += item.quantity;
          acc.volume += item.volume;
          acc.weight += item.weight;
          return acc;
        }, { quantity: 0, volume: 0, weight: 0 });
        if (contract.freightUnit === freightUnit.per_kg && configOptions.ceilWeightVolumePayment) {
          weight = Math.ceil(weight);
          volume = Math.ceil(volume);
        }

        // Extract material code from the line item key to look up slabs
        const lineItemKeyArr = lineItemKey.split('::');
        const materialCategory = lineItemKeyArr[lineItemKeyArr.length - 1];
        // For backward compatibility, look up slabs by material code or use default
        let freightCategory = this
          .getContractBasedOnSlabs(skuCategoryRates, materialCategory, indentMovement);
        if (!freightCategory.length) {
          freightCategory = this.getContractBasedOnSlabs(skuCategoryRates, 'default', indentMovement);
        }
        const unitToBeConsidered = (contract.freightUnit === freightUnit.per_case) ?
          quantity : Math.max(weight, volume * contract.multiplyingFactor);
        if (params.volmetricWeightMap) {
          groupByLineItem[lineItemKey].forEach((material) => {
            const volmetricWeightKey = Helper.getKeyForVolmetricWeightMap(
              material.materialCode,
              material.lineItem === 'NA' ? null : material.lineItem,
              material._id,
            );
            params.volmetricWeightMap[volmetricWeightKey] = unitToBeConsidered;
          });
        }
        logger.info(`[freight.js] :- The cost to be considered for getting slab rate is ${unitToBeConsidered} for line item ${lineItemKey}`);
        if (freightCategory && freightCategory.length) {
          let freightChargeDetails;
          if (contract.freightUnit === freightUnit.per_case) {
            freightChargeDetails = Freight.getFreightBasedOnSlab(
              unitToBeConsidered,
              freightCategory,
              false,
              { ignoreMinRate: true },
            );
          } else {
            freightChargeDetails = Freight.getFreightBasedOnSlabPerKg(
              unitToBeConsidered,
              freightCategory,
              false,
              { ignoreMinRate: true },
            );
          }
          return freightChargeDetails.freightCharge;
        }

        const chargeName = contract.baseFreight
          ? Helper.getChargeName(contract.freightUnit) : 'freightCharge';
        return (contract[chargeName] * unitToBeConsidered);
      }).reduce((acc, rate) => acc + rate, 0);
    } else {
      materialCost = lineItemGroups.map((lineItemKey) => {
        // Extract material code from the line item key
        const lineItemKeyArr = lineItemKey.split('::');
        const materialCategory = lineItemKeyArr[lineItemKeyArr.length - 1];
        // For backward compatibility, look up contracts by material code or use default
        let freightCategory = Helper
          .getContractWithoutSlabs(skuCategoryRates, materialCategory, indentMovement);
        if (!freightCategory) {
          freightCategory = Helper.getContractWithoutSlabs(skuCategoryRates, 'default', indentMovement);
        }
        const freightRate = (freightCategory && freightCategory.rate) ?
          freightCategory.rate : contract.freightCharge;
        return groupByLineItem[lineItemKey].reduce((acc, item) =>
          acc + item.quantity, 0) * freightRate;
      }).reduce((acc, rate) => acc + rate, 0);
    }
    return materialCost;
  }

  calculateCostForMaterialCategorySupportedFreightUnits(indentMovement, contract, destination,
    materialMap, packageMap, clientConfig, sourceGateId, sourceLocationId, destinationGateId,
    quantityUnitsForPieces, params, configOptions = {}) {
    let materials = Helper
      .getMaterialsForDestination(destination, sourceGateId, destinationGateId, sourceLocationId);
    const { payment_settings: paymentSettings } = clientConfig;
    const packages = Helper.getPackages(materials);
    let packageCost;
    let groupBycategory;
    let groupByLineItem;
    let calculateVolumetricWeightAtMaterialLevel = false;
    const volumetricWeightAtMaterialLvlConfig = (paymentSettings && paymentSettings.calculate_volumetric_weight_at_material_level)
      || configOptions.calculate_volumetric_weight_at_material_level;
    if (!isEmpty(volumetricWeightAtMaterialLvlConfig)) {
      if (params.deliveryType === DeliveryType.PTL) {
        calculateVolumetricWeightAtMaterialLevel = volumetricWeightAtMaterialLvlConfig.enabled_for_ptl;
      } else {
        calculateVolumetricWeightAtMaterialLevel = volumetricWeightAtMaterialLvlConfig.enabled_for_ftl;
      }
    }
    /*
      calculate_volumetric_weight_at_material_level this config is handled for
      -> per kg and per case contracts
      -> cost will be calculated at material code + line item + category level instead of just category level
      -> min rate will be applied at destination level instead of category level
    */
    const isEligibleForMaterialLvlWtVolCalc = calculateVolumetricWeightAtMaterialLevel
      && volumetricWeightAtMaterialLevelFreightUnits.includes(contract.freightUnit);
    if (contract.freightUnit === freightUnit.per_case) {
      materials = Helper
        .getMaterialsForDestinationBasedOnLooseMaterials(materials, materialMap,
          clientConfig);

      if (isEligibleForMaterialLvlWtVolCalc) {
        groupByLineItem = Helper.groupedMaterialQuantityByLineItem(materials, materialMap,
          quantityUnitsForPieces);
      } else {
        groupBycategory = Helper.groupedMaterialQuantityPerMaterialCategory(materials, materialMap,
          quantityUnitsForPieces);
      }

      packageCost = Helper.calculatePackageCost(packages, packageMap, contract,
        Helper.getChargeName(contract.freightUnit),
        quantityUnitsForPieces);
    } else {
      // get material quantity by material category for per kg
      // get material invoice amount by material category for percentage
      // if flag ignore_material_category_for_per_kg enabled then material category will be ignored
      // for calculations per_kg contracts
      let ignoreCategoryForPerKg = false;
      if (contract.freightUnit === freightUnit.per_kg && paymentSettings &&
        paymentSettings.ignore_material_category_for_per_kg) {
        ignoreCategoryForPerKg = true;
      }

      if (isEligibleForMaterialLvlWtVolCalc) {
        // Use new line item based grouping when flag is enabled
        groupByLineItem = Helper.groupedMaterialUnitsByLineItem(materials);
      } else {
        // Maintain existing behavior - group by category
        groupBycategory = Helper.groupedMaterialUnitsPerMaterialCategory(materials,
          ignoreCategoryForPerKg);
      }
    }

    const rates = contract.skuCategoryRates || [];
    const skuCategoryRates = sortBy(rates, ['slab.min']);
    let materialCost;
    let materialSlabs = [];
    if (isEligibleForMaterialLvlWtVolCalc) { // per kg and per case
      materialCost = this.calculateForwardTotalCostAtMaterialLineItemLvl(
        contract,
        groupByLineItem,
        skuCategoryRates,
        indentMovement,
        paymentSettings,
        configOptions,
        params,
      );
      // outof all available slabs whatever is maximum min rate need to compare with calculated cost
      materialSlabs = skuCategoryRates;
    } else {
      // Original implementation for backward compatibility
      const materialCategoriesGroup = Object.keys(groupBycategory);

      if (contract.freightUnit === freightUnit.percentage) {
        const { totalAmount, slabs } = Helper.calculateFreightBasedOnMaterialInvoiceAmount(materialCategoriesGroup,
          skuCategoryRates, indentMovement, groupBycategory, contract);
        materialCost = totalAmount;
        materialSlabs = slabs;
      } else if ((paymentSettings && paymentSettings.product_category_based_on_slab) ||
        [freightUnit.per_kg].includes(contract.freightUnit)) {
        materialCost = materialCategoriesGroup.map((category) => {
          const quantity = groupBycategory[category].reduce((acc, cate) =>
            acc + cate.quantity, 0);
          let weight = groupBycategory[category].reduce((acc, cate) =>
            acc + cate.weight, 0);
          let volume = groupBycategory[category].reduce((acc, cate) =>
            acc + cate.volume, 0);
          if (contract.freightUnit === freightUnit.per_kg && configOptions.ceilWeightVolumePayment) {
            weight = Math.ceil(weight);
            volume = Math.ceil(volume);
          }
          let freightCategory = this.getContractBasedOnSlabs(skuCategoryRates, category, indentMovement);
          if (!freightCategory.length) {
            freightCategory = this.getContractBasedOnSlabs(skuCategoryRates, 'default', indentMovement);
          }
          const unitToBeConsidered = (contract.freightUnit === freightUnit.per_case) ?
            quantity : Math.max(weight, volume * contract.multiplyingFactor);
          logger.info(`[freight.js] :- The cost to be considered for getting slab rate is ${unitToBeConsidered}`);
          if (freightCategory && freightCategory.length) {
            const { freightCharge, slabs } = contract.freightUnit === freightUnit.per_case ? (Freight.getFreightBasedOnSlab(unitToBeConsidered,
              freightCategory)) : (Freight.getFreightBasedOnSlabPerKg(unitToBeConsidered,
              freightCategory));
            materialSlabs = materialSlabs.concat(slabs);
            return freightCharge;
          }
          const chargeName = contract.baseFreight
            ? Helper.getChargeName(contract.freightUnit) : 'freightCharge';
          return (contract[chargeName] * unitToBeConsidered);
        }).reduce((acc, rate) => acc + rate, 0);
      } else {
        materialCost = materialCategoriesGroup.map((category) => {
          let freightCategory = Helper.getContractWithoutSlabs(skuCategoryRates, category, indentMovement);
          if (!freightCategory) {
            freightCategory = Helper.getContractWithoutSlabs(skuCategoryRates, 'default', indentMovement);
          }
          if (freightCategory) {
            materialSlabs = materialSlabs.concat([freightCategory]);
          }
          const freightRate = (freightCategory && freightCategory.rate) ?
            freightCategory.rate : contract.freightCharge;
          return groupBycategory[category].reduce((acc, cate) =>
            acc + cate.quantity, 0) * freightRate;
        }).reduce((acc, rate) => acc + rate, 0);
      }
    }
    const maxofSkuCategoryMinRates = materialSlabs.reduce((acc, _slab) => Math.max(_slab.min_rate, acc), 0);
    logger.info(`[PAY] - Selected maxofSkuCategoryMinRates - ${maxofSkuCategoryMinRates}`);
    const freightMinRate = maxofSkuCategoryMinRates || contract.minRate;
    let calculatedCost = materialCost;
    if ([freightUnit.per_case].includes(contract.freightUnit)) {
      calculatedCost = (packages && packages.length) ? packageCost : materialCost;
    }
    logger.info(`[PAY] - Calculated Total Cost - ${calculatedCost}`);
    
    // If indent status is 40, return 0 for freight charge
    if (params && params.indentStatus === 40) {
      logger.info(`[PAY] - Indent status is 40, setting freight charge to 0`);
      return 0;
    }
    
    return Math.max(calculatedCost, freightMinRate);
  }

  calculateReturnFreightCharge(contract, returnFreightUnitGroups, destination,
    materialMap, packageMap, clientConfig, sourceGateId, sourceLocationId, destinationGateId,
    quantityUnitsForPieces, configOptions = {}, lastMileReturnPrimaryContractIds = []) {
    const returnFreightUnitGroup = returnFreightUnitGroups;
    if (contract) {
      const contractId = contract.contractId ? contract.contractId.toString() : null;
      if (!returnFreightUnitGroup[contract.freightUnit]) {
        returnFreightUnitGroup[contract.freightUnit] = 0;
      }
      if (contract.freightUnit === freightUnit.per_trip
        || contract.freightUnit === freightUnit.per_km) {
        if (contractId && (returnFreightUnitGroup[contract.freightUnit] === 0 || returnFreightUnitGroup[contract.freightUnit] < Math.max(contract.returnFreightRate, contract.minRate))) {
          if (lastMileReturnPrimaryContractIds[0]) {
            lastMileReturnPrimaryContractIds[0] = contractId;
          } else {
            lastMileReturnPrimaryContractIds.push(contractId);
          }
        }
        returnFreightUnitGroup[contract.freightUnit] = Math.max(
          Math.max(contract.returnFreightRate, contract.minRate),
          returnFreightUnitGroup[contract.freightUnit]);
      } else {
        const unitType = contract.freightUnit;
        let destinationMaterials = Helper.getMaterialsForDestination(destination, sourceGateId, destinationGateId, sourceLocationId);
        if ([freightUnit.per_package, freightUnit.per_box, freightUnit.per_case].includes(unitType)) {
          destinationMaterials = Helper
            .getMaterialsForDestinationBasedOnLooseMaterials(destinationMaterials, materialMap,
              clientConfig);
        }
        returnFreightUnitGroup[contract.freightUnit] += Math.max(Helper.calculateQuantity(
          destinationMaterials, unitType, materialMap, packageMap,
          contract, 'returnFreightRate', quantityUnitsForPieces, configOptions), contract.minRate);
        if (contractId) {
          lastMileReturnPrimaryContractIds.push(contractId);
        }
      }
    }
    return returnFreightUnitGroup;
  }

  calculateForwardFreightCharge(indentMovement, status, contract, freightUnitGroups, deliveryType, destination,
    materialMap, packageMap, clientConfig, sourceGateId, sourceLocationId, destinationGateId, quantityUnitsForPieces, params = {}, configOptions = {},
    contractFreightAmountAndDestinationMap = {}, primaryContractIds = []) {
    const freightUnitGroup = freightUnitGroups;
    const materialCategorySupportedFreightUnits = [freightUnit.per_case, freightUnit.per_kg, freightUnit.percentage];
    let hillyRegionCharge = 0;
    if (contract) {
      const contractId = contract.contractId ? contract.contractId.toString() : null;
      if (!freightUnitGroup[contract.freightUnit]) {
        freightUnitGroup[contract.freightUnit] = 0;
      }
      if (status === indentStatus.CANCELLED) {
        if (deliveryType === DeliveryType.PTL) {
          freightUnitGroup[contract.freightUnit] += contract.cancellationCharge;
          if (contractId) {
            primaryContractIds.push(contractId);
          }
        } else {
          if (contractId && (freightUnitGroup[contract.freightUnit] === 0 || freightUnitGroup[contract.freightUnit] < contract.cancellationCharge)) {
            if (primaryContractIds[0]) {
              primaryContractIds[0] = contractId;
            } else {
              primaryContractIds.push(contractId);
            }
          }
          freightUnitGroup[contract.freightUnit] = Math.max(contract.cancellationCharge,
            freightUnitGroup[contract.freightUnit]);
        }
        return freightUnitGroup;
      }
      if (contract.freightUnit === freightUnit.per_trip || contract.freightUnit === freightUnit.per_km) {
        if (contractId && (freightUnitGroup[contract.freightUnit] === 0 || freightUnitGroup[contract.freightUnit] < Math.max(contract.freightCharge, contract.minRate))) {
          if (primaryContractIds[0]) {
            primaryContractIds[0] = contractId;
          } else {
            primaryContractIds.push(contractId);
          }
        }
        freightUnitGroup[contract.freightUnit] = Math.max(
          Math.max(contract.freightCharge, contract.minRate),
          freightUnitGroup[contract.freightUnit]);
        contractFreightAmountAndDestinationMap[destination.locationId] = Math.max(
          Math.max(contract.freightCharge, contract.minRate));
      } else if (materialCategorySupportedFreightUnits.includes(contract.freightUnit)) {
        const calculatedCostCurrentDestination = this.calculateCostForMaterialCategorySupportedFreightUnits(
          indentMovement, contract, destination,
          materialMap, packageMap, clientConfig, sourceGateId,
          sourceLocationId, destinationGateId, quantityUnitsForPieces, { deliveryType, indentStatus: status, ...params }, configOptions,
        );
        contractFreightAmountAndDestinationMap[destination.locationId] = calculatedCostCurrentDestination;
        freightUnitGroup[contract.freightUnit] += calculatedCostCurrentDestination;
        if (contractId) {
          primaryContractIds.push(contractId);
        }
        if (contract.hillyRegionPercentage) {
          hillyRegionCharge = (contract.hillyRegionPercentage) ?
            (calculatedCostCurrentDestination) * (contract.hillyRegionPercentage / 100) : 0;
          if (hillyRegionCharge && calculatedCostCurrentDestination) {
            if (!freightUnitGroup.hillyRegionCharge) {
              freightUnitGroup.hillyRegionCharge = hillyRegionCharge;
            } else {
              freightUnitGroup.hillyRegionCharge += hillyRegionCharge;
            }
          }
        }
      } else {
        const unitType = contract.freightUnit;
        let destinationMaterials = Helper.getMaterialsForDestination(destination, sourceGateId, destinationGateId, sourceLocationId);
        if (contract.freightUnit === freightUnit.per_package ||
          contract.freightUnit === freightUnit.per_box) {
          destinationMaterials = Helper
            .getMaterialsForDestinationBasedOnLooseMaterials(destinationMaterials, materialMap,
              clientConfig);
        }
        // passing maximum freight charge across all destinations - PAN-20330
        if (contract.freightUnit === freightUnit.per_ton && deliveryType === DeliveryType.FTL) {
          const { maxFreightCharge = 0, minRateOfMaxFreightChargeContract = 0 } = params;
          contract.freightCharge = maxFreightCharge;
          contract.minRate = minRateOfMaxFreightChargeContract;
          freightUnitGroup[contract.freightUnit] += Helper.calculateQuantity(
            destinationMaterials, unitType, materialMap, packageMap,
            contract, 'freightCharge', quantityUnitsForPieces, params);
          return freightUnitGroup;
        }
        const chargeName = (unitType === freightUnit.per_kg && contract.baseFreight)
          ? 'baseFreight' : 'freightCharge';
        freightUnitGroup[contract.freightUnit] += Math.max(Helper.calculateQuantity(
          destinationMaterials, unitType, materialMap, packageMap,
          contract, chargeName, quantityUnitsForPieces, params), contract.minRate);
        if (contractId) {
          primaryContractIds.push(contractId);
        }
      }
    }
    return freightUnitGroup;
  }

  getChargeBasedOnFreightUnit(freightUnitGroup) {
    if (freightUnitGroup[freightUnit.per_trip]) {
      return {
        freightUnit: freightUnit.per_trip,
        freightCharge: freightUnitGroup[freightUnit.per_trip],
      };
    }
    if (freightUnitGroup[freightUnit.per_km]) {
      return {
        freightUnit: freightUnit.per_km,
        freightCharge: freightUnitGroup[freightUnit.per_km],
      };
    }

    const freightCharges = Object.values(freightUnitGroup);
    if (freightCharges.length) {
      const freightCharge = Math.max(...Object.values(freightUnitGroup));
      const unit = Object.keys(freightUnitGroup).reduce((a, b) => (
        freightUnitGroup[a] > freightUnitGroup[b] ? a : b));
      return { freightUnit: unit, freightCharge };
    }
    return { freightUnit: null, freightCharge: 0 };
  }
  calculateRtoChargeBasedOnFreightUnit(materials,
    locationData, contract, indentMovement, materialMap,
    clientConfig, quantityUnitsForPieces, configOptions = {}) {
    let calculatedCharge = 0;
    if (materials && materials.length) {
      const materialData = cloneDeep(materials);
      let currentLocationMaterials = materialData.filter((material) => {
        let condition = (locationData.sourceLocationId.toString()
          === material.source_location_id.toString())
          && (locationData.locationId.toString() === material.destination_location_id.toString());
        if (locationData.sourceGateId) {
          condition = condition
            && (locationData.sourceGateId.toString() === material.source_gate_id.toString());
        }
        if (locationData.gateId) {
          condition = condition
            && (locationData.gateId.toString() === material.destination_gate_id.toString());
        }
        return condition;
      });
      if (currentLocationMaterials && currentLocationMaterials.length) {
        currentLocationMaterials = Helper.getNormalizedMaterialDetails(currentLocationMaterials);
        if (contract.freight_unit === freightUnit.per_case) {
          currentLocationMaterials = Helper
            .getMaterialsForDestinationBasedOnLooseMaterials(currentLocationMaterials, materialMap,
              clientConfig);
        }
        const { payment_settings: paymentSettings } = clientConfig;
        let ignoreCategoryForPerKg = false;
        if (contract.freightUnit === freightUnit.per_kg && paymentSettings &&
          paymentSettings.ignore_material_category_for_per_kg) {
          ignoreCategoryForPerKg = true;
        }
        let groupByCategory = {};
        if (contract.freight_unit === freightUnit.per_case) {
          groupByCategory = Helper.groupedMaterialQuantityPerMaterialCategory(
            currentLocationMaterials,
            materialMap,
            quantityUnitsForPieces,
          );
        } else {
          groupByCategory = Helper.groupedMaterialUnitsPerMaterialCategory(
            currentLocationMaterials, ignoreCategoryForPerKg);
        }
        const materialCategoriesGroup = Object.keys(groupByCategory);
        const rates = contract.sku_rates || [];
        const skuCategoryRates = sortBy(rates, ['slab.min']);
        let materialCost;
        if ((paymentSettings && paymentSettings.product_category_based_on_slab) ||
          [freightUnit.per_kg].includes(contract.freight_unit)) {
          materialCost = materialCategoriesGroup.map((category) => {
            const quantity = groupByCategory[category].reduce((acc, item) =>
              acc + item.quantity, 0);
            let weight = groupByCategory[category].reduce((acc, item) =>
              acc + item.weight, 0);
            let volume = groupByCategory[category].reduce((acc, item) =>
              acc + item.volume, 0);
            if (contract.freight_unit === freightUnit.per_kg &&
              configOptions.ceilWeightVolumePayment) {
              weight = Math.ceil(weight);
              volume = Math.ceil(volume);
            }
            let freightCategory = this.getContractBasedOnSlabs(skuCategoryRates,
              category, indentMovement);
            if (!freightCategory.length) {
              freightCategory = this.getContractBasedOnSlabs(skuCategoryRates, 'default', indentMovement);
            }
            const unitToBeConsidered = (contract.freight_unit === freightUnit.per_case) ?
              quantity : Math.max(weight,
                (contract.multiplying_factor && (volume * contract.multiplying_factor)) || 0);
            const isRtoCharge = true;
            const { freightCharge } = contract.freight_unit === freightUnit.per_case ?
              (Freight.getFreightBasedOnSlab(unitToBeConsidered, freightCategory, isRtoCharge)) :
              (Freight.getFreightBasedOnSlabPerKg(
                unitToBeConsidered, freightCategory, isRtoCharge));
            return freightCharge;
          });
          calculatedCharge = materialCost.reduce((acc, rate) => acc + rate, 0);
        } else {
          materialCost = materialCategoriesGroup.map((category) => {
            let freightCategory = Helper.getContractWithoutSlabs(
              skuCategoryRates, category, indentMovement);
            if (!freightCategory) {
              freightCategory = Helper.getContractWithoutSlabs(skuCategoryRates, 'default', indentMovement);
            }
            const rtoCharges = (freightCategory && freightCategory.rto_charges) ?
              freightCategory.rto_charges : contract.rto_charges;
            const charge = groupByCategory[category].reduce((acc, cate) =>
              acc + cate.quantity, 0) * rtoCharges;
            return Math.max((freightCategory.minimum_rto_charges ? freightCategory.minimum_rto_charges : 0), charge);
          });
          calculatedCharge = materialCost.reduce((acc, rate) => acc + rate, 0) || 0;
        }
      }
    }
    return calculatedCharge;
  }

  getRtoCharges(data, maxFreightChargeContract, materialMap,
    indentMovement, clientConfig, quantityUnitsForPieces, configOptions = {}) {
    let rtoCharges = 0;
    if (data.indentDestinations && data.indentDestinations.length) {
      if (data.deliveryType === DeliveryType.FTL) {
        // Only for Per Trip
        const notDeliveredDrop = data.indentDestinations.find(drop => drop.isNotDelivered === true);
        if (!isEmpty(notDeliveredDrop)) {
          rtoCharges = Math.max(maxFreightChargeContract.rtoCharges, maxFreightChargeContract.minimumRtoCharges);
        }
      } else if (data.deliveryType === DeliveryType.PTL) {
        // Only for Per Case and Per Kg
        if (data.destinations && data.destinations.length) {
          const currentLocationData = data.indentDestinations.filter((item) => {
            const filter = (
              (item.locationId.toString() === data.destinations[0].locationId.toString()) &&
              item.isNotDelivered === true
            );
            // if (data.destinations[0].locationId && data.destinations[0].destinationGateId) {
            //   return ((item.locationId.toString() === data.destinations[0].locationId.toString()
            //     && item.gateId.toString() === data.destinations[0].destinationGateId.toString()) &&
            //     item.isNotDelivered === true);
            // }
            return filter;
          });
          if (currentLocationData.length) {
            let calculatedCharge = 0;
            currentLocationData.forEach((item) => {
              if (item.isNotDelivered || data.destinations[0].isNotDelivered) {
                // rtoCharges = data.destinations[0].contract.rto_charges || 0;
                const contract = item.contract;
                const materials = data.destinations[0].materials || [];
                calculatedCharge += this.calculateRtoChargeBasedOnFreightUnit(
                  materials,
                  item,
                  contract,
                  indentMovement,
                  materialMap,
                  clientConfig,
                  quantityUnitsForPieces,
                  configOptions,
                );
              }
            });
            rtoCharges = calculatedCharge;
          }
        }
      }
    }
    return rtoCharges;
  }

  getMovementBasedBuildToPartyId(data, defaultBillToParty, billToPartyId) {
    if (!data || !billToPartyId) {
      return null;
    }

    if (defaultBillToParty === 2 && billToPartyId) {
      return billToPartyId.toString();
    } else if (defaultBillToParty === 1 && data.sources && data.sources.length > 0
       && data.sources[0].locationId) {
      return data.sources[0].locationId.toString();
    } else if (defaultBillToParty === 3) {
      return null;
    }
    return billToPartyId;
  }

  getDefaultBillToParty(data, billToPartyId) {
    if (!data || !billToPartyId) {
      return null;
    }
    const movementTypes = [2, 3, 4];
    let result = null;

    if (movementTypes.includes(data.movement) && billToPartyId) {
      result = billToPartyId.toString();
    } else if (data.sources && data.sources.length && data.sources[0].locationId) {
      result = data.sources[0].locationId.toString();
    }

    return result;
  }

  getBillToPartyId(data, clientConfig, maxBaseFreightDestinationId) {
    if (!data) {
      return null;
    }

    const indentMovement = data.movement ? InvertedMovements[data.movement] : null;
    if (!indentMovement) {
      return null;
    }

    // Use destinationWithMaxFreight if available, otherwise use maxFreightChargeContract
    let billToPartyLocationId = null;

    if (maxBaseFreightDestinationId) {
      billToPartyLocationId = maxBaseFreightDestinationId;
    }

    const defaultBillToParty = clientConfig &&
                              clientConfig.default_bill_to_party &&
                              clientConfig.default_bill_to_party[indentMovement];

    return defaultBillToParty
      ? this.getMovementBasedBuildToPartyId(data,
        defaultBillToParty, billToPartyLocationId)
      : this.getDefaultBillToParty(data, billToPartyLocationId);
  }

  freightRateMaximum(data, clientConfig = {}, configOptions = {}, volmetricWeightMap = {}) {
    const deliveryType = data.deliveryType;
    const status = data.status;
    const indentMovement = InvertedMovements[data.movement];
    let freightUnitGroup = {};
    let returnFreightUnitGroup = {};
    const freightContracts = data.freightCharges;
    const returnFreightContracts = data.returnFreightCharges;
    const quantityUnitsForPieces = Helper
      .getQuantityUnitsForPieces(data.customQuantityUnits);
    const materialMap = Helper.fetchMaterialMap(data.materials.masterData);
    const packageMap = Helper.fetchPackageMap(data.packages.masterData);
    if (freightContracts.length && data.orderType === orderType.OPEN) {
      const freightContract = freightContracts[0];
      let freightCharge = freightContract.freightCharge;
      const openfreightUnit = freightContract.freightUnit;
      if (openfreightUnit === freightUnit.per_trip) {
        return { freightUnit: freightUnit.per_trip, freightCharge };
      }
      freightCharge = 0;
      data.destinations.forEach((destination) => {
        freightCharge += Helper.calculateQuantity(
          Helper.getNormalizedMaterialDetails(destination.materials),
          openfreightUnit, materialMap, packageMap, freightContract, 'freightCharge', quantityUnitsForPieces, configOptions);
      });
      return { freightUnit: openfreightUnit, freightCharge };
    }

    // added filter since secondary consignees will not have gate id
    const gateIds = [...new Set(freightContracts.filter(contract => contract.sourceGateId)
      .map(contract => contract.sourceGateId.toString()))];
    const maxFreightCharge = Math.max(...freightContracts.map((contract) => {
      if (contract.freightUnit === freightUnit.per_kg) {
        return contract.baseFreight || contract.freightCharge;
      }
      return contract.freightCharge;
    }));
    const maxFreightChargeContract = freightContracts.find(contract =>
      contract.freightCharge === maxFreightCharge ||
      contract.baseFreight === maxFreightCharge);


    const minRateOfMaxFreightChargeContract = maxFreightChargeContract.minRate;


    // introducing params variable to send extra fields to calculateForwardFreightCharge function
    const params = {
      maxFreightCharge,
      minRateOfMaxFreightChargeContract,
      volmetricWeightMap,
    };
    const contractFreightAmountAndDestinationMap = {};
    const indentContracts = [...returnFreightContracts, ...freightContracts];
    const primaryContractIds = [];
    const lastMileReturnPrimaryContractIds = [];
    data.destinations.forEach((destination) => {
      const contracts = indentContracts.filter(_contract => _contract.destinationId.toString() === destination.locationId.toString());
      contracts.forEach((contract) => {
        logger.info(`debug contract - ${JSON.stringify(contract)}`);
        const sourceGateId = contract.sourceGateId;
        const destinationGateId = contract.destinationGateId;
        const sourceLocationId = contract.sourceId;
        if (contract.isLastMileReturn) {
          returnFreightUnitGroup = this.calculateReturnFreightCharge(contract,
            returnFreightUnitGroup, destination, materialMap, packageMap,
            clientConfig, sourceGateId, sourceLocationId, destinationGateId, quantityUnitsForPieces,
            configOptions, lastMileReturnPrimaryContractIds);
        } else {
          freightUnitGroup = this.calculateForwardFreightCharge(indentMovement, status, contract,
            freightUnitGroup, deliveryType, destination, materialMap, packageMap,
            clientConfig, sourceGateId, sourceLocationId, destinationGateId, quantityUnitsForPieces, params, configOptions,
            contractFreightAmountAndDestinationMap, primaryContractIds);
        }
      });
    });
    data.billToPartyAmount = contractFreightAmountAndDestinationMap;

    let returnFreightCharge = 0;
    if (data.consider_return_freight_charge) {
      let highestFreightCharge = 0;
      let correspondingReturnFreightRate = 0;
      data.destinations.forEach((destination) => {
        const freightCharges = destination.contract.freight_charges || 0;
        const returnFreightRate = destination.contract.return_freight_rate || 0;
        if (freightCharges >= highestFreightCharge) {
          highestFreightCharge = freightCharges;
          correspondingReturnFreightRate = returnFreightRate;
        }
      });
      returnFreightCharge = correspondingReturnFreightRate;
    }
    if (Object.prototype.hasOwnProperty.call(freightUnitGroup, freightUnit.per_ton)) {
      if (maxFreightChargeContract.contractId &&
        freightUnitGroup[freightUnit.per_ton] < minRateOfMaxFreightChargeContract) {
        if (primaryContractIds[0]) {
          primaryContractIds[0] = maxFreightChargeContract.contractId.toString();
        } else {
          primaryContractIds.push(maxFreightChargeContract.contractId.toString());
        }
      }
      freightUnitGroup[freightUnit.per_ton] = Math.max(freightUnitGroup[freightUnit.per_ton],
        minRateOfMaxFreightChargeContract);
    }
    if (status === indentStatus.CANCELLED) {
      // Indent cancellation charge calculated only for forward leg
      // Return 0 for return leg
      if (isEmpty(freightUnitGroup)) {
        return { freightUnit: null, freightCharge: 0 };
      }
      const freightCharge = (deliveryType === DeliveryType.FTL) ? Math.max(...Object.values(freightUnitGroup))
        : Object.values(freightUnitGroup).reduce((acc, value) => acc + value, 0);

      const unit = Object.keys(freightUnitGroup).reduce((a, b) =>
        (freightUnitGroup[a] > freightUnitGroup[b] ? a : b));
      return { freightUnit: unit, freightCharge: freightCharge.toFixed(2) };
    }
    const forwardCharge = this.getChargeBasedOnFreightUnit(freightUnitGroup);
    const returnCharge = this.getChargeBasedOnFreightUnit(returnFreightUnitGroup);
    const totalCost = forwardCharge.freightCharge + returnCharge.freightCharge;
    data.primaryContractIds = [...primaryContractIds, ...lastMileReturnPrimaryContractIds];
    let rtoCharges = 0;
    if (data.isLastMileEnabled) {
      rtoCharges = this.getRtoCharges(data, maxFreightChargeContract,
        materialMap, indentMovement, clientConfig, quantityUnitsForPieces, configOptions);
    }
    // Hilly region charge computation
    let hillyRegionCharge = 0;
    const restrictMaxContractBasedHillyUpdate = [forwardCharge.freightUnit, returnCharge.freightUnit].includes(freightUnit.per_kg.toString()) || [forwardCharge.freightUnit, returnCharge.freightUnit].includes(freightUnit.per_case.toString());
    if (deliveryType === DeliveryType.FTL && totalCost && !restrictMaxContractBasedHillyUpdate) {
      hillyRegionCharge = (maxFreightChargeContract.hillyRegionPercentage) ?
        ((totalCost) * (maxFreightChargeContract.hillyRegionPercentage / 100)) : 0;
    } else if (deliveryType === DeliveryType.PTL && !restrictMaxContractBasedHillyUpdate) {
      const hillyPercentage = (data && data.destinations[0] && data.destinations[0].contract && !!data.destinations[0].contract.hilly_region_addition_percentage)
        ? data.destinations[0].contract.hilly_region_addition_percentage : 0;
      hillyRegionCharge = (hillyPercentage) ?
        ((totalCost) * (hillyPercentage / 100)) : 0;
    } else {
      hillyRegionCharge = freightUnitGroup.hillyRegionCharge;
    }
    // Rov Charge computation
    let rovCharge = 0;
    if (deliveryType === DeliveryType.PTL && maxFreightChargeContract.rovChargePercentage) {
      const totalMaterialInvoiceAmount = data.destinations.reduce((sum, dest) =>
        sum + (dest.materials || []).reduce(
          (s, m) => s + Number((m.invoice && m.invoice.amount) || 0), 0), 0);
      const calculatedRovCharge = totalMaterialInvoiceAmount * (maxFreightChargeContract.rovChargePercentage / 100);
      rovCharge = Math.max(calculatedRovCharge, maxFreightChargeContract.minimumRovCharge || 0);
    }
    return {
      freightUnit: forwardCharge.freightUnit,
      freightCharge: totalCost.toFixed(2),
      rtoCharges,
      hillyRegionCharge,
      rovCharge: rovCharge.toFixed(2),
      returnFreightCharge,
    };
  }
}

module.exports = Freight;
